import Logo from '@/assets/logo-primary.svg'
import CollapsedFlyout from '@/components/ui/collapsed-flyout'
import { useAuthContext } from '@/context'
import { interviewCoachApi } from '@/services/interview-coach/api'
import { generalItems, isAdvancedNavigationUser, menuItems, type NavigationVisibility } from '@/layout/const'
import { isDev, isProd, isStaging } from '@/utils/env'
import { ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FocusEvent as ReactFocusEvent, MouseEvent as ReactMouseEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
    applyInfluencerCampaignHighlights,
    applySituationsHighlight,
    ROUTE_LAYER_MAP,
    SIDEBAR_GROUPS,
    subItemMatchesPath,
    type SidebarMenuItem,
    type SidebarSubMenuItem,
    type SidebarSubMenuItemWithMatch,
} from './sidebar-support'
import { ConnectedSubItems, MenuGlyph } from './sidebar-widgets'

const Sidebar = ({ isCollapsed }: { isCollapsed: boolean }) => {
    const { user, loggedIn, isLoading: authLoading, org, isGuest } = useAuthContext()
    const location = useLocation()
    const navigate = useNavigate()
    const sidebarRef = useRef<HTMLDivElement>(null)

    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
        const defaults: Record<string, boolean> = {}
        menuItems.forEach(item => {
            if ((item as SidebarMenuItem & { defaultExpanded?: boolean }).defaultExpanded && item.subItems?.length) {
                defaults[item.id_element] = true
            }
        })
        return defaults
    })
    const [hoveredItemId, setHoveredItemId] = useState<string | null>(null)
    const [knowledgeReviewPendingCount, setKnowledgeReviewPendingCount] = useState(0)
    const [hoveredFlyout, setHoveredFlyout] = useState<{
        id: string
        title: string
        link?: string
        rect: DOMRect
        subItems?: SidebarSubMenuItem[]
    } | null>(null)
    const flyoutRef = useRef<HTMLDivElement | null>(null)

    const activeEnvironment = useMemo(() => {
        if (isDev) return 'development'
        if (isStaging) return 'staging'
        if (isProd) return 'production'
        return undefined
    }, [])

    const shouldShowItem = useCallback(
        (environments: string[]) => {
            if (!environments?.length) return true
            if (!activeEnvironment) return false
            return environments.includes(activeEnvironment)
        },
        [activeEnvironment]
    )

    const is500RocketsUser = useMemo(() => !!user?.email?.toLowerCase().endsWith('@500rockets.io'), [user?.email])
    const experienceVariant = user?.experience_variant
    const capabilities = useMemo(() => user?.capabilities || {}, [user?.capabilities])
    const isMobileConsumer = experienceVariant === 'mobile_consumer'

    const isAdmin = useMemo(
        () => !!user?.isAdmin || !!user?.email?.toLowerCase().endsWith('@500rockets.io'),
        [user?.isAdmin, user?.email]
    )
    const canSeeAdvancedNavigation = useMemo(() => isAdvancedNavigationUser(user), [user])

    const filteredMenuItems = useMemo(() => {
        const hiddenForMobileConsumer = new Set([
            'sidebar-seo', // 2HQ
            'sidebar-visual-engine',
            'sidebar-visual-studios',
            'sidebar-situations',
        ])

        const prunedLibrarySubRoutes = new Set([
            '/dashboard/content-sheets',
            '/dashboard/datasets',
            '/dashboard/flows/my-workflows',
            '/dashboard/flows/results',
            '/dashboard/flows/store',
        ])

        return menuItems
            .filter(item => {
                const navigationVisibility = (item as unknown as { navigationVisibility?: NavigationVisibility })
                    .navigationVisibility
                if (navigationVisibility === 'parked') return false
                if (navigationVisibility !== 'core' && !canSeeAdvancedNavigation) return false
                const envOk = shouldShowItem(item.environments)
                const adminOk = !item.isAdminRoute || isAdmin
                if (!envOk || !adminOk) return false

                // Capability-gated modules should never render as dead sidebar buttons.
                // AuthContext can briefly hydrate from persisted state; capabilities are the source of truth.
                if (item.id_element === 'sidebar-seo' && capabilities.can_use_2hq === false) return false
                if (item.id_element === 'sidebar-visual-engine' && capabilities.can_use_visual_engine === false)
                    return false
                if (item.id_element === 'sidebar-visual-studios' && capabilities.can_use_visual_studios === false)
                    return false
                if (item.id_element === 'sidebar-situations' && capabilities.can_use_situations === false) return false
                if (item.id_element === 'sidebar-seo') return is500RocketsUser

                if (isMobileConsumer) {
                    if (hiddenForMobileConsumer.has(item.id_element)) return false
                    if (item.id_element === 'sidebar-agent' && capabilities.can_use_agent === false) return false
                    if (item.id_element === 'sidebar-documents' && capabilities.can_use_documents === false)
                        return false
                    if (item.id_element === 'sidebar-identity' && capabilities.can_use_identity_engine === false)
                        return false
                    if (item.id_element === 'sidebar-strategic' && capabilities.can_use_strategy_assets === false)
                        return false
                    if (item.id_element === 'sidebar-brand-baseline' && capabilities.can_use_strategy_assets === false)
                        return false
                    // Mobile consumer: we are moving forward with Deliverable Studio; remove Utilities/Campaigns parent.
                    if (item.id_element === 'sidebar-campaigns') return false
                }

                // Studio is currently scoped to mobile-consumer experience.
                if (item.id_element === 'sidebar-deliverable-studio') return isMobileConsumer

                return true
            })
            .map(item => {
                if (!isMobileConsumer) return item
                if (item.id_element !== 'sidebar-library' || !item.subItems?.length) return item
                return {
                    ...item,
                    subItems: item.subItems.filter(sub => !prunedLibrarySubRoutes.has(sub.link)),
                }
            })
    }, [shouldShowItem, isAdmin, is500RocketsUser, isMobileConsumer, capabilities, canSeeAdvancedNavigation])

    useEffect(() => {
        if (authLoading || !loggedIn || isGuest || !org?.id) {
            setKnowledgeReviewPendingCount(0)
            return
        }

        let mounted = true
        void interviewCoachApi
            .listKnowledgeReviewQueue()
            .then(queue => {
                if (mounted) setKnowledgeReviewPendingCount(queue.pendingCount)
            })
            .catch(() => undefined)
        return () => {
            mounted = false
        }
    }, [authLoading, isGuest, loggedIn, org?.id])

    const navigationMenuItems = useMemo(
        () =>
            filteredMenuItems.map(item => {
                if (item.id_element === 'sidebar-knowledge-review' && knowledgeReviewPendingCount > 0) {
                    return { ...item, badge: String(knowledgeReviewPendingCount) }
                }
                if (item.id_element !== 'sidebar-interview-coach' || !item.subItems?.length) return item
                return {
                    ...item,
                    subItems: item.subItems.map(sub =>
                        sub.link === '/dashboard/knowledge-review' && knowledgeReviewPendingCount > 0
                            ? { ...sub, badge: String(knowledgeReviewPendingCount) }
                            : sub
                    ),
                }
            }),
        [filteredMenuItems, knowledgeReviewPendingCount]
    )

    const filteredGeneralItems = useMemo(
        () =>
            generalItems.filter(item => {
                const navigationVisibility = (item as unknown as { navigationVisibility?: NavigationVisibility })
                    .navigationVisibility
                if (navigationVisibility === 'parked') return false
                if (navigationVisibility !== 'core' && !canSeeAdvancedNavigation) return false
                return (!item.isAdminRoute || isAdmin) && shouldShowItem(item.environments)
            }),
        [shouldShowItem, isAdmin, canSeeAdvancedNavigation]
    )

    const activeLayer = useMemo(() => {
        const pathname = location.pathname
        const match = Object.entries(ROUTE_LAYER_MAP)
            .filter(([route]) => pathname.startsWith(route))
            .sort((a, b) => b[0].length - a[0].length)[0]
        return match?.[1] ?? null
    }, [location.pathname])

    const groupedItems = useMemo(
        () =>
            SIDEBAR_GROUPS.map(group => ({
                ...group,
                items: navigationMenuItems.filter(
                    item => (item as SidebarMenuItem & { group?: string }).group === group.key
                ),
            })).filter(g => g.items.length > 0),
        [navigationMenuItems]
    )

    const currentPath = useMemo(() => location.pathname.split('/').slice(0, 4).join('/'), [location.pathname])

    const isActiveRoute = useCallback(
        (match: string[]) =>
            match.some(route => {
                if (route === '') return location.pathname === '/dashboard' || location.pathname === '/dashboard/'
                return currentPath.startsWith(`/dashboard/${route}`)
            }),
        [currentPath, location.pathname]
    )

    const linkMatchesLocation = useCallback(
        (target: string) => {
            const [path, rawSearch] = target.split('?')
            const search = rawSearch ? `?${rawSearch}` : ''
            if (search) {
                return location.pathname === path && location.search === search
            }
            return location.pathname.startsWith(path)
        },
        [location.pathname, location.search]
    )

    const subItemMatches = useCallback(
        (subItem: SidebarSubMenuItemWithMatch, pathname: string) => subItemMatchesPath(subItem, pathname),
        []
    )

    const toggleSection = useCallback((id: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [id]: !prev[id],
        }))
    }, [])

    const isSectionExpanded = useCallback((id: string) => expandedSections[id] ?? false, [expandedSections])

    useEffect(() => {
        if (isCollapsed) return

        filteredMenuItems.forEach(item => {
            if (!item.subItems?.length) return
            const hasActiveSubItem = item.subItems.some(sub =>
                subItemMatches(sub as SidebarSubMenuItemWithMatch, location.pathname)
            )
            if (!hasActiveSubItem) return
            setExpandedSections(prev => {
                if (prev[item.id_element]) return prev
                return { ...prev, [item.id_element]: true }
            })
        })
    }, [location.pathname, isCollapsed, filteredMenuItems, subItemMatches])

    const handleCollapsedEnter = useCallback(
        (id: string, title: string, link: string, element: HTMLElement, subNav?: SidebarSubMenuItem[]) => {
            if (!isCollapsed) return
            const rect = element.getBoundingClientRect()
            setHoveredItemId(id)
            setHoveredFlyout({ id, title, link, rect, subItems: subNav })
        },
        [isCollapsed]
    )

    const handleCollapsedLeave = useCallback(
        (event: ReactMouseEvent<HTMLDivElement> | ReactFocusEvent<HTMLDivElement>, id: string) => {
            const currentTarget = event.currentTarget
            const nextTarget = event.relatedTarget as Node | null
            if (nextTarget instanceof Node && currentTarget.contains(nextTarget)) return
            if (nextTarget instanceof Node && flyoutRef.current?.contains(nextTarget)) return
            setHoveredItemId(prev => (prev === id ? null : prev))
            setHoveredFlyout(prev => (prev?.id === id ? null : prev))
        },
        []
    )

    const handleFlyoutLeave = useCallback(
        (event: ReactMouseEvent<HTMLDivElement> | ReactFocusEvent<HTMLDivElement>) => {
            const nextTarget = event.relatedTarget as Node | null
            if (!hoveredFlyout) return
            if (nextTarget instanceof Node) {
                if (nextTarget instanceof HTMLElement && nextTarget.dataset.sidebarItem === hoveredFlyout.id) return
                if (flyoutRef.current?.contains(nextTarget)) return
            }
            setHoveredItemId(prev => (prev === hoveredFlyout.id ? null : prev))
            setHoveredFlyout(null)
        },
        [hoveredFlyout]
    )

    const hoveredFlyoutId = hoveredFlyout?.id

    const updateFlyoutPosition = useCallback(() => {
        if (!hoveredFlyoutId) return
        const element = document.querySelector<HTMLElement>(`[data-sidebar-item='${hoveredFlyoutId}']`)
        if (!element) return
        const rect = element.getBoundingClientRect()
        setHoveredFlyout(prev => (prev && prev.id === hoveredFlyoutId ? { ...prev, rect } : prev))
    }, [hoveredFlyoutId])

    const getCollapsedHandlers = useCallback(
        (id: string, title: string, link: string, subNav?: SidebarSubMenuItem[]) => {
            if (!isCollapsed) return {}
            return {
                onMouseEnter: (event: ReactMouseEvent<HTMLDivElement>) =>
                    handleCollapsedEnter(id, title, link, event.currentTarget, subNav),
                onMouseLeave: (event: ReactMouseEvent<HTMLDivElement>) => handleCollapsedLeave(event, id),
                onFocus: (event: ReactFocusEvent<HTMLDivElement>) =>
                    handleCollapsedEnter(id, title, link, event.currentTarget, subNav),
                onBlur: (event: ReactFocusEvent<HTMLDivElement>) => handleCollapsedLeave(event, id),
            }
        },
        [handleCollapsedEnter, handleCollapsedLeave, isCollapsed]
    )

    useEffect(() => {
        const handleResize = () => {
            if (sidebarRef.current) {
                sidebarRef.current.style.height = `${window.innerHeight}px`
            }
        }
        handleResize()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        if (isCollapsed) return
        setHoveredItemId(null)
        setHoveredFlyout(null)
    }, [isCollapsed])

    useEffect(() => {
        if (!isCollapsed || !hoveredFlyoutId) return
        updateFlyoutPosition()
        const handleUpdate = () => updateFlyoutPosition()
        window.addEventListener('resize', handleUpdate)
        window.addEventListener('scroll', handleUpdate, true)
        const sidebarElement = sidebarRef.current
        sidebarElement?.addEventListener('scroll', handleUpdate)
        return () => {
            window.removeEventListener('resize', handleUpdate)
            window.removeEventListener('scroll', handleUpdate, true)
            sidebarElement?.removeEventListener('scroll', handleUpdate)
        }
    }, [hoveredFlyoutId, isCollapsed, updateFlyoutPosition])

    const getItemClasses = useCallback(
        ({ isHighlighted, isHovered }: { isHighlighted: boolean; isHovered: boolean }) => {
            const paddingClass = isCollapsed ? 'px-2' : isHighlighted ? 'px-4' : 'px-2'
            const justifyClass = isCollapsed ? 'justify-center' : 'justify-start'
            const collapsedClasses = isCollapsed
                ? isHighlighted || isHovered
                    ? 'border border-[#481C4F] bg-white text-primary-main shadow-[0px_8px_24px_rgba(72,28,79,0.12)]'
                    : 'border border-transparent text-[#585858]'
                : ''
            const expandedClasses = !isCollapsed
                ? isHighlighted
                    ? 'bg-[#E4DEE5] text-primary-main'
                    : 'text-[#585858]'
                : ''
            return `flex items-center w-full h-12 rounded-xl transition-all duration-500 ${paddingClass} ${justifyClass} ${collapsedClasses} ${expandedClasses}`
        },
        [isCollapsed]
    )

    const renderItem = (item: SidebarMenuItem) => {
        const { icon: IconOrSrc, name, link, id_element, match, subItems } = item
        const badge = (item as SidebarMenuItem & { badge?: string }).badge
        const hasSubItems = Boolean(subItems?.length)
        const isExpanded = isSectionExpanded(id_element)
        let isRouteActive = isActiveRoute(match)
        let isLinkActive = linkMatchesLocation(link)

        const influencerHl = applyInfluencerCampaignHighlights(id_element, location.pathname, location.search)
        if (influencerHl?.forceLinkActive) isLinkActive = true
        if (influencerHl?.suppressRouteActive) isRouteActive = false

        // Visual Studios live under /dashboard/campaigns but should not highlight the Campaigns parent item.
        if (id_element === 'sidebar-campaigns' && location.pathname.startsWith('/dashboard/campaigns/visual-studios')) {
            isRouteActive = false
            isLinkActive = false
        }

        // Deliverable Studio lives under /dashboard/campaigns/utilities but has its own nav item.
        // Avoid "double highlight" on the Utilities parent when Studio is active.
        if (
            id_element === 'sidebar-campaigns' &&
            location.pathname.startsWith('/dashboard/campaigns/utilities/studio')
        ) {
            isRouteActive = false
            isLinkActive = false
        }

        // Rhetorical Workbench has its own sidebar item under Construct.
        if (
            id_element === 'sidebar-campaigns' &&
            location.pathname.startsWith('/dashboard/campaigns/rhetorical-workbench')
        ) {
            isRouteActive = false
            isLinkActive = false
        }

        if (applySituationsHighlight(id_element, location.pathname)) {
            isRouteActive = true
            isLinkActive = true
        }

        // Rhetorical Workbench — only highlight on its own routes, never bleed from /campaigns/*
        if (id_element === 'sidebar-rhetorical-workbench') {
            const onWorkbench = location.pathname.startsWith('/dashboard/campaigns/rhetorical-workbench')
            isRouteActive = onWorkbench
            isLinkActive = onWorkbench
        }

        const hasActiveSubItem =
            subItems?.some(sub => subItemMatches(sub as SidebarSubMenuItemWithMatch, location.pathname)) ?? false
        const shouldHighlight = (!isExpanded || isCollapsed) && (isRouteActive || isLinkActive || hasActiveSubItem)
        const isCollapsedHover = isCollapsed && hoveredItemId === id_element
        const collapsedHandlers = getCollapsedHandlers(id_element, name, link, subItems)

        if (hasSubItems) {
            const parentClasses = `${getItemClasses({
                isHighlighted: shouldHighlight,
                isHovered: isCollapsedHover,
            })} cursor-pointer`

            return (
                <div key={id_element} data-sidebar-item={id_element} {...collapsedHandlers}>
                    <div
                        className={parentClasses}
                        onClick={() => !isCollapsed && toggleSection(id_element)}
                        aria-expanded={!isCollapsed && isExpanded}
                        role={isCollapsed ? undefined : 'button'}
                        tabIndex={isCollapsed ? undefined : 0}
                        onKeyDown={event => {
                            if (isCollapsed) return
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                toggleSection(id_element)
                            }
                        }}
                    >
                        <Link to={link} title={name} className='flex-shrink-0'>
                            <MenuGlyph icon={IconOrSrc} name={name} className='w-[18px] h-[18px]' />
                        </Link>
                        {!isCollapsed && (
                            <>
                                <span className='ml-3 text-sm font-medium'>{name}</span>
                                <ChevronDown
                                    className={`ml-auto w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                />
                            </>
                        )}
                    </div>

                    {!isCollapsed && isExpanded && subItems?.length ? (
                        <ConnectedSubItems
                            subItems={subItems}
                            pathname={location.pathname}
                            subItemMatches={subItemMatches}
                            isCollapsed={isCollapsed}
                        />
                    ) : null}
                </div>
            )
        }

        return (
            <div key={id_element} data-sidebar-item={id_element} {...collapsedHandlers}>
                <Link
                    to={link}
                    className={getItemClasses({
                        isHighlighted: isRouteActive || isLinkActive,
                        isHovered: isCollapsedHover,
                    })}
                    id={id_element}
                    aria-current={isRouteActive || isLinkActive ? 'page' : undefined}
                >
                    <MenuGlyph icon={IconOrSrc} name={name} className='flex-shrink-0 w-[18px] h-[18px]' />
                    <span className={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : 'block'}`}>
                        {name}
                        {badge ? (
                            <span className='ml-2 rounded-full bg-[#63316B] px-1.5 py-0.5 text-[10px] font-bold text-white'>
                                {badge}
                            </span>
                        ) : null}
                    </span>
                </Link>
            </div>
        )
    }

    return (
        <nav
            ref={sidebarRef}
            className={`flex flex-col ${isCollapsed ? 'items-center' : 'items-start'} space-y-2 pt-4 pb-8 h-full overflow-y-auto`}
        >
            <div
                onClick={() => navigate('/dashboard/home')}
                className='flex flex-shrink-0 w-full px-4 mt-8 mb-8 cursor-pointer'
            >
                <img src={Logo} alt='Brand logo' className='text-center' />
            </div>

            <div className='flex flex-col flex-1 w-full gap-1 px-4 mt-12 overflow-y-auto'>
                {groupedItems.map(group => {
                    const isActiveGroup = group.key === activeLayer
                    return (
                        <div key={group.key} className='mb-4'>
                            {!isCollapsed && (
                                <div
                                    className={`text-[11px] uppercase tracking-wide mb-1 flex items-center px-1 ${isActiveGroup ? 'text-violet-500 font-semibold' : 'text-slate-400'}`}
                                >
                                    <span>{group.label}</span>
                                </div>
                            )}
                            {group.items.map(item => renderItem(item))}
                        </div>
                    )
                })}

                <span className='text-xs font-medium text-[#AAAAAA] pl-1 pt-6 pb-4'>GENERAL</span>

                {filteredGeneralItems.map(({ icon: GIconOrSrc, name, link, id_element, match }) => {
                    const isRouteActive = isActiveRoute(match)
                    const isCollapsedHover = isCollapsed && hoveredItemId === id_element
                    const collapsedHandlers = getCollapsedHandlers(id_element, name, link)

                    return (
                        <div key={id_element} data-sidebar-item={id_element} {...collapsedHandlers}>
                            <Link
                                to={link}
                                className={getItemClasses({
                                    isHighlighted: isRouteActive,
                                    isHovered: isCollapsedHover,
                                })}
                                id={id_element}
                                aria-current={isRouteActive ? 'page' : undefined}
                            >
                                <MenuGlyph icon={GIconOrSrc} name={name} className='flex-shrink-0 w-[18px] h-[18px]' />
                                <span className={`ml-3 text-sm font-medium ${isCollapsed ? 'hidden' : 'block'}`}>
                                    {name}
                                </span>
                            </Link>
                        </div>
                    )
                })}

                {isCollapsed && hoveredFlyout ? (
                    <CollapsedFlyout
                        title={hoveredFlyout.title}
                        link={hoveredFlyout.link}
                        rect={hoveredFlyout.rect}
                        innerRef={flyoutRef}
                        onLeave={handleFlyoutLeave}
                    >
                        {hoveredFlyout.subItems?.length ? (
                            <div className='flex flex-col gap-1'>
                                {hoveredFlyout.subItems.map((subItem, idx) => {
                                    const linkMatches = subItemMatches(subItem, location.pathname)
                                    const isFirstWithThisLink =
                                        hoveredFlyout.subItems!.findIndex(s => s.link === subItem.link) === idx
                                    const isSubItemActive = linkMatches && isFirstWithThisLink
                                    return (
                                        <Link
                                            key={`${subItem.name}-${idx}`}
                                            to={subItem.link}
                                            className={`rounded-2xl px-4 py-2 text-sm transition-colors ${
                                                isSubItemActive
                                                    ? 'bg-[#E5DAF1] text-[#481C4F] font-medium'
                                                    : 'text-[#585858] hover:bg-[#F4EEF6]'
                                            }`}
                                        >
                                            {subItem.name}
                                        </Link>
                                    )
                                })}
                            </div>
                        ) : null}
                    </CollapsedFlyout>
                ) : null}
            </div>

            <div className='flex-shrink-0 w-full px-4 mt-auto'>{null}</div>
        </nav>
    )
}

export default Sidebar
